import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function Newsletter() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const { data } = await api.post("/subscribe", { email, name });
      toast.success(data.message || "Subscribed!");
      setEmail("");
      setName("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Subscription failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="newsletter" data-testid="newsletter" className="relative z-10 border-t border-border overflow-hidden">
      <div className="absolute inset-0 fin-grid opacity-40 pointer-events-none" />
      <div className="absolute inset-0 fin-glow pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="font-mono-accent text-xs uppercase tracking-[0.25em] mb-5 text-primary">/ The Newsletter</p>
            <h2 className="font-display font-black uppercase tracking-tighter leading-[0.85] text-5xl sm:text-6xl lg:text-7xl">
              Design your<br /><span className="text-primary">cashflow</span>
            </h2>
            <p className="mt-6 text-base sm:text-lg max-w-md leading-relaxed text-muted-foreground">
              Join the mailing list. Get cashflow models, hedging playbooks and disciplined
              investing frameworks — delivered to your inbox.
            </p>
          </div>

          <motion.form
            onSubmit={submit}
            data-testid="newsletter-form"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-card border border-border p-8 sm:p-10 space-y-5"
          >
            <div>
              <label className="block font-mono-accent text-[11px] uppercase tracking-[0.15em] text-muted-foreground mb-2">Name (optional)</label>
              <input
                data-testid="newsletter-name-input"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                className="w-full bg-background border border-border focus:border-primary px-4 py-3.5 outline-none transition-colors"
              />
            </div>
            <div>
              <label className="block font-mono-accent text-[11px] uppercase tracking-[0.15em] text-muted-foreground mb-2">Email</label>
              <input
                data-testid="newsletter-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com"
                className="w-full bg-background border border-border focus:border-primary px-4 py-3.5 outline-none transition-colors"
              />
            </div>
            <button
              data-testid="newsletter-submit-button"
              type="submit"
              disabled={loading}
              className="group w-full inline-flex items-center justify-center gap-3 bg-primary text-primary-foreground font-display font-bold uppercase tracking-wide py-4 hover:bg-[#1AE39C] transition-colors disabled:opacity-60"
            >
              {loading ? "Subscribing..." : "Join the Kingdom"}
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </button>
            <p className="text-[11px] text-muted-foreground leading-relaxed">
              For educational purposes only. Investments are subject to market risks.
            </p>
          </motion.form>
        </div>
      </div>
    </section>
  );
}
